import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { GripVertical, Target, Pencil, Check } from 'lucide-react';
import {
  API, PIPELINE_STAGES, computePipelineStage, stagePatchBody, TEMP_META,
  effectiveTemp, computeNBA, fmtMoney, PIPELINE_GOAL_KEY,
} from './helpers';

const COL_ACCENT = {
  blue: 'bg-blue-500', sky: 'bg-sky-500', cyan: 'bg-cyan-500', violet: 'bg-violet-500',
  indigo: 'bg-indigo-500', orange: 'bg-orange-500', amber: 'bg-amber-500',
  emerald: 'bg-emerald-500', slate: 'bg-slate-400',
};

export const PipelineBoard = ({ leads, token, onOpen, onMoved }) => {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [goal, setGoal] = useState(() => Number(localStorage.getItem(PIPELINE_GOAL_KEY)) || 0);
  const [editGoal, setEditGoal] = useState(false);
  const [goalInput, setGoalInput] = useState('');

  const columns = useMemo(() => {
    const map = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.key, []]));
    leads.forEach((l) => { const k = computePipelineStage(l); if (map[k]) map[k].push(l); });
    return map;
  }, [leads]);

  const pipelineValue = useMemo(() => leads
    .filter((l) => !['lost', 'converted'].includes(computePipelineStage(l)))
    .reduce((sum, l) => sum + (Number(l.property_target_price_max) || 0), 0), [leads]);
  const activeCount = useMemo(() => leads.filter((l) => !['lost', 'converted'].includes(computePipelineStage(l))).length, [leads]);

  const saveGoal = () => {
    const v = Number(goalInput.replace(/[^0-9]/g, '')) || 0;
    setGoal(v); localStorage.setItem(PIPELINE_GOAL_KEY, String(v)); setEditGoal(false);
  };
  const goalPct = goal > 0 ? Math.min(100, Math.round((pipelineValue / goal) * 100)) : 0;

  const move = async (leadId, stageKey) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || computePipelineStage(lead) === stageKey) return;
    try {
      const r = await fetch(`${API}/api/nurture/leads/${leadId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(stagePatchBody(stageKey)),
      });
      if (!r.ok) throw new Error();
      toast.success(`Moved to ${PIPELINE_STAGES.find((s) => s.key === stageKey)?.label}`);
      onMoved && onMoved();
    } catch { toast.error('Move failed'); }
  };

  return (
    <div className="space-y-4">
      {/* Pipeline volume goal strip */}
      <div className="rounded-2xl bg-white border border-slate-200/80 p-4 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.05)]" data-testid="pipeline-goal-strip">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.15em] text-slate-400">Pipeline value</p>
              <p className="text-2xl font-black font-mono text-slate-900" data-testid="pipeline-value">{fmtMoney(pipelineValue) || '$0'}</p>
            </div>
            <div className="h-9 w-px bg-slate-200" />
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.15em] text-slate-400">Active leads</p>
              <p className="text-2xl font-black font-mono text-slate-900">{activeCount}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-violet-500" />
            {editGoal ? (
              <div className="flex items-center gap-1.5">
                <input autoFocus value={goalInput} onChange={(e) => setGoalInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveGoal()}
                  placeholder="e.g. 5000000" data-testid="pipeline-goal-input"
                  className="w-32 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-violet-100" />
                <button type="button" onClick={saveGoal} data-testid="pipeline-goal-save" className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-600 text-white hover:bg-violet-700"><Check className="w-4 h-4" /></button>
              </div>
            ) : (
              <button type="button" onClick={() => { setGoalInput(goal ? String(goal) : ''); setEditGoal(true); }} data-testid="pipeline-goal-edit"
                className="inline-flex items-center gap-1.5 text-[12px] font-bold text-slate-600 hover:text-slate-900">
                <span className="font-mono">Goal {goal > 0 ? fmtMoney(goal) : 'not set'}</span><Pencil className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
        {goal > 0 && (
          <div className="mt-3">
            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden" role="progressbar" aria-valuenow={goalPct} aria-valuemin={0} aria-valuemax={100}>
              <motion.div initial={{ width: 0 }} animate={{ width: `${goalPct}%` }} transition={{ duration: 0.6 }}
                className={`h-full rounded-full ${goalPct >= 100 ? 'bg-emerald-500' : 'bg-violet-500'}`} />
            </div>
            <p className="text-[11px] font-bold text-slate-400 mt-1.5 font-mono">{goalPct}% of monthly goal</p>
          </div>
        )}
      </div>

      <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto pb-3" data-testid="pipeline-board">
        <div className="flex gap-3 w-max">
        {PIPELINE_STAGES.map((s) => {
          const items = columns[s.key] || [];
          const vol = items.reduce((sum, l) => sum + (Number(l.property_target_price_max) || 0), 0);
          const isOver = overCol === s.key;
          return (
            <div key={s.key} data-testid={`kanban-column-${s.key}`}
              onDragOver={(e) => { e.preventDefault(); setOverCol(s.key); }}
              onDragLeave={() => setOverCol((c) => (c === s.key ? null : c))}
              onDrop={(e) => { e.preventDefault(); if (dragId) move(dragId, s.key); setDragId(null); setOverCol(null); }}
              className={`w-[270px] flex-shrink-0 rounded-2xl p-2.5 transition-colors ${isOver ? 'bg-rose-50 ring-2 ring-rose-300' : 'bg-slate-100/70 ring-1 ring-slate-200/70'}`}>
              <div className="flex items-center gap-2 px-1.5 py-1 mb-2">
                <span className={`w-2 h-2 rounded-full ${COL_ACCENT[s.color] || 'bg-slate-400'}`} />
                <p className="text-[12px] font-black text-slate-800">{s.label}</p>
                <span className="text-[11px] font-bold text-slate-400 ml-auto">{items.length}</span>
              </div>
              {vol > 0 && <p className="px-1.5 mb-2 text-[10px] font-bold font-mono text-slate-400">{fmtMoney(vol)} volume</p>}
              <div className="space-y-2 min-h-[60px]">
                {items.map((l) => {
                  const temp = TEMP_META[effectiveTemp(l)];
                  const nba = computeNBA(l);
                  const est = fmtMoney(l.property_target_price_max);
                  return (
                    <motion.div key={l.id} layout
                      draggable
                      onDragStart={() => setDragId(l.id)}
                      onDragEnd={() => { setDragId(null); setOverCol(null); }}
                      onClick={() => onOpen(l.id)}
                      data-testid={`kanban-card-${l.id}`}
                      className={`group cursor-pointer rounded-xl bg-white border border-slate-200/80 p-2.5 shadow-sm hover:shadow-md hover:border-slate-300 transition-[box-shadow,border-color] ${dragId === l.id ? 'opacity-40' : ''}`}>
                      <div className="flex items-start gap-1.5">
                        <GripVertical className="w-3.5 h-3.5 text-slate-300 mt-0.5 flex-shrink-0 group-hover:text-slate-400" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${temp.badge} flex-shrink-0`} />
                            <p className="text-[13px] font-bold text-slate-900 truncate">{l.name || 'Unnamed'}</p>
                            {est && <span className="ml-auto text-[10px] font-mono font-bold text-slate-500">{est}</span>}
                          </div>
                          <p className="text-[10px] text-slate-500 mt-1 truncate">{nba.label}</p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
                {items.length === 0 && <p className="text-[11px] text-slate-400 text-center py-4">—</p>}
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
};
